// import React from 'react';
import { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Stack,
    RadioGroup,
    Radio,
    Center,
    Text,
    GridItem,
    Select
} from '@chakra-ui/react';
import CV2ProcyonChart from './CV2ProcyonChart';
import data from './cv2-results.json';


const CV2Metrics = () => {
    const models = [
        { name: 'ConvNeXt-Tiny', value: 'convnext' }, 
        { name: 'BLIP (Base)', value: 'blip' }, 
        { name: 'DETR (Base)', value: 'detr' }, 
        { name: 'SAM 2.1 (Small)', value: 'sam2.1' }, 
        { name: 'Real-ESRGAN', value: 'esrgan' }
    ]

    const defaultModel = 'convnext';
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 991);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
    const [display, setDisplay] = useState('chart')
    const [isActiveLegend, setIsActiveLegend] = useState(null);
    const [optSelected, setOptSelected] = useState(defaultModel);
    const [dataArr, setDataArr] = useState(data.results[defaultModel].datasets);
    const [labelArr, setLabelArr] = useState(data.results.labels);
    const [titleData, setTitleData] = useState(getModelName(defaultModel));
    const [textData, setTextData] = useState('Top-1 accuracy')
    const [titleBox, setTitleBox] = useState(getModelName(defaultModel));
    const [textContentBox, setTextContentBox] = useState('The Top-1 accuracy metric is used to evaluate image classification models. The Top-1 accuracy means that the model’s answer with the highest confidence matches the ground truth answer.');

    const arrayColors = ["#5b0428", "#122c49"];

    function getModelName(value) {
        const model = models.find(model => model.value === value);
        return model ? model.name : '';
    }

    const onOptionSelected = (opt) => {
        if (typeof opt === 'string' || opt instanceof String) {
        } else {
            opt = opt.target.value
        }
        let modelName = opt
        setOptSelected(modelName);
        setDataArr(data.results[modelName].datasets);
        setLabelArr(data.results.labels);
        setTitleData(getModelName(modelName));
        setIsActiveLegend(null);
        setTitleBox(getModelName(modelName));
        
        switch (opt) {
            case 'convnext':
                setTextData('Top-1 accuracy');
                setTextContentBox('The Top-1 accuracy metric is used to evaluate image classification models. The Top-1 accuracy means that the model’s answer with the highest confidence matches the ground truth answer.');
                break;
            case 'blip':
                setTextData('CIDEr score and BLEU4 score');
                setTextContentBox('The CIDEr score and BLEU4 score metrics are used to evaluate image captioning models. The CIDEr score measures the similarity between the generated captions and the reference captions, while the BLEU4 score evaluates the precision of n-grams in the generated captions.');
                break;
            case 'detr':
                setTextData('Mean Average Precision (mAP)');
                setTextContentBox('The Mean Average Precision (mAP) metric is used to evaluate object detection models. It measures the precision and recall of the model\'s predictions across different classes.');
                break;
            case 'esrgan':
                setTextData('SSIM and PSNR');
                setTextContentBox('The SSIM (Structural Similarity Index) and PSNR (Peak Signal-to-Noise Ratio) metrics are used to evaluate image super-resolution models. SSIM measures the similarity between the original and super-resolved images, while PSNR evaluates the quality of the super-resolved image compared to the original image.');
                break;
            case 'sam2.1':
                setTextData('Average IoU score');
                setTextContentBox('The Average IoU (Intersection over Union) score is used to evaluate segmentation models. It measures the overlap between the predicted segmentation and the ground truth across different classes.');
                break;
            default:
                break;
        }
    }

    const onDisplaySelected = (opt) => {
        if (typeof opt === 'string' || opt instanceof String) {
        } else {
            opt = opt.target.value
        }
        setDisplay(opt)
    }

    const onClickLegend = (index) => {
        setIsActiveLegend(index);
    }

    const renderLegend = (dataArr) => {
        return dataArr.map((item, index) => {
            const containerLevel = 'ContainerLevel' + index;
            const level = 'Level' + index;
            return (<Box key={index} onClick={() => onClickLegend(index)} cursor='pointer' id={containerLevel} display={{ base: 'inline-block', lg: 'inline-block', md: 'inline-block', sm: 'inline-block' }} pr={{ base: '15px', lg: '30px', md: '15px', sm: '15px' }} >
                <Box id={level} display='inline-block' backgroundColor={arrayColors[index]} width='20px' height='20px'></Box> <Text fontWeight='600' className='verticalAlignTop' fontSize='12px' display='inline'>{item.type}</Text>
            </Box>);
        })
    }

    useEffect(() => { // check mobile and desktop version. render legend always in mobile as it's always visible the chart
        const handleResize = () => {
            setIsDesktop(window.innerWidth > 991);
            setIsMobile(window.innerWidth <= 991);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <Box textAlign="center" fontSize="xl">
            <Grid
                /*templateAreas={`"header header"
          "nav main"
          "footer footer"`}*/
                templateAreas={{ base: '"header header" "nav nav" "main main" "footer footer"', lg: '"header header" "nav main""footer footer"', md: '"header header" "nav nav" "main main" "footer footer"', sm: '"header header" "nav nav" "main main" "footer footer"' }}
                gridTemplateRows={{ base: '50px 1fr 1fr 0px', lg: '50px 1fr 30px', md: '50px 1fr 320px 0px', sm: '50px 1fr 1fr 0px' }}
                gridTemplateColumns={'320px 1fr'}
                h='200px'
                gap='0'
                color='blackAlpha.700'
            >
                <GridItem pl={{ base: '20px', lg: '40px', md: '30px', sm: '20px' }} area={'header'} display='inline-grid' alignItems='center' textAlign='left' borderBottom='1px' borderBottomColor='#E7E7E7' fontSize='15px' color='#59595A' fontWeight='600'>
                    Quality metrics from Procyon AI Computer Vision 2 Models
                </GridItem>
                <GridItem pl={{ base: '0px', lg: '20px', md: '10px', sm: '0px' }} area={'nav'} borderRight={{ base: '0px', lg: '1px', md: '0px', sm: '0px' }} borderRightColor={{ base: '#E7E7E7', lg: '#E7E7E7', md: '#E7E7E7', sm: '#E7E7E7' }} fontSize='15px' color='#59595A'>
                    <Box display={{ base: 'block', lg: 'none', md: 'block', sm: 'block' }} pl={{ base: '20px', lg: '0', md: '20px', sm: '20px' }} pr={{ base: '20px', lg: '0', md: '20px', sm: '20px' }} mt={{ base: '20px', lg: '0', md: '20px', sm: '20px' }}>
                        <Text fontSize='15px' display={{ base: 'inline', lg: 'inline', md: 'inline', sm: 'inline' }}>{textData} {(optSelected === 'yolov3' || optSelected === 'esrgan') ? 'metric' : 'metrics'} with</Text> <Text display={{ base: 'inline', lg: 'none', md: 'inline', sm: 'inline' }} fontSize='15px' color='#C02033' fontWeight='bold'>{titleData}</Text>
                    </Box>
                    <Center>
                        <Box pt={{ base: '20px', lg: '40px', md: '30px', sm: '30px' }} pl={{ base: '0px', lg: '40px', md: '0px', sm: '0px' }} pr={{ base: '0px', lg: '40px', md: '0px', sm: '0px' }} pb={{ base: '15px', lg: '40px', md: '20px', sm: '15px' }} width={{ base: '90%', lg: 'auto', md: '70%', sm: '85%' }}>
                            <Text marginBottom={{ base: '15px', lg: '20px', md: '10px', sm: '10px' }} textAlign='left'>Model</Text>
                            <RadioGroup value={optSelected} onChange={onOptionSelected} width='210px' display={{ base: 'none', lg: 'block', md: 'none', sm: 'none' }}>
                                <Stack>
                                    {models.map((model) => (
                                        <Radio pb={{ base: '15px', lg: '10px', md: '10px', sm: '10px' }} size="sm" colorScheme='red' value={model.value} key={model.value}>{model.name}</Radio>
                                    ))}
                                </Stack>
                            </RadioGroup>
                            <Select className='chooseBenchmark' value={optSelected} onChange={onOptionSelected} display={{ base: 'block', lg: 'none', md: 'block', sm: 'block' }}>
                                {models.map((model) => (
                                    <option value={model.value} key={model.value}>{model.name}</option>
                                ))}
                            </Select>
                            {isDesktop && <Text mt={{ base: '20px', lg: '40px', md: '20px', sm: '20px' }} marginBottom={{ base: '10px', lg: '15px', md: '10px', sm: '10px' }} textAlign='left'>Display</Text>}
                            <RadioGroup onChange={onDisplaySelected} value={display} display={{ base: 'none', lg: 'block', md: 'none', sm: 'none' }}>
                                <Stack>
                                    <Radio pb={{ base: '15px', lg: '10px', md: '10px', sm: '10px' }} size="sm" value="chart" colorScheme='red'>Chart</Radio>
                                    <Radio size="sm" value="table" colorScheme='red'>Table</Radio>
                                </Stack>
                            </RadioGroup>
                            {isDesktop && <Select onChange={onDisplaySelected} value={display} display={{ base: 'block', lg: 'none', md: 'block', sm: 'block' }}>
                                <option value="chart">Chart</option>
                                <option value="table">Table</option>
                            </Select>}
                        </Box>
                    </Center>
                </GridItem>
                <GridItem pl={{ base: '15px', lg: '30px', md: '15px', sm: '15px' }} pr={{ base: '15px', lg: 'auto', md: 'auto', sm: '15px' }} pt={{ base: '5px', lg: '40px', md: '20px', sm: '20px' }} area={'main'} fontSize='15px' textAlign='left' width={{ base: '100%', lg: 'auto', md: '70%', sm: '100%' }} ml={{ base: 'auto', lg: '30px', md: 'auto', sm: 'auto' }} mr={{ base: 'auto', lg: 'auto', md: 'auto', sm: 'auto' }}>
                    <Box display={{ base: 'block', lg: 'block', md: 'block', sm: 'block' }}>
                        <Text fontSize='15px' display={{ base: 'none', lg: 'inline', md: 'none', sm: 'none' }}>{textData} {(optSelected === 'yolov3' || optSelected === 'esrgan') ? 'metric' : 'metrics'} with</Text> <Text display={{ base: 'none', lg: 'inline', md: 'none', sm: 'none' }} fontSize='15px' color='#C02033' fontWeight='bold'>{titleData}</Text>
                    </Box>
                    <Box textAlign='left' className='verticalAlignTop' display={{ base: 'block', lg: 'block', md: 'block', sm: 'block' }} pr={{ base: 'auto', lg: '30px', md: 'auto', sm: 'auto' }} pl={{ base: 'auto', lg: '30px', md: 'auto', sm: 'auto' }} pt={{ base: '10px', lg: '30px', md: '20px', sm: '10px' }}>
                        {((isMobile) || (isDesktop && display === 'chart')) && renderLegend(dataArr)}
                    </Box>
                    <Box id='graphContainer' display={{ base: 'block', lg: 'inline-block', md: 'block', sm: 'block' }} position={{ base: 'relative', lg: 'relative', md: 'relative', sm: 'relative' }} width={{ base: 'auto', xl: '900px', lg: '700px', md: '700px', sm: 'auto' }} height={display === 'chart' ? { base: '600px', lg: '600px', md: '600px', sm: '600px' } : { base: '750px', lg: '750px', md: '900px', sm: '900px' }} mt={{ base: '10px', lg: '10px', md: '10px', sm: '10px' }} mb={{ base: '20px', lg: '40px', md: '30px', sm: '20px' }}>
                        <CV2ProcyonChart chartData={dataArr} labelsChartData={labelArr} titleBenchmark={titleData} clickedLegendIndex={isActiveLegend} displayOption={display}>
                        </CV2ProcyonChart>
                    </Box>
                    <Box width={{ base: 'auto', xl: '700px', lg: '600px', md: '600px', sm: 'auto' }} mt='30px' padding='15px' backgroundColor='#fff' borderRadius='6px' border='2px' borderStyle='solid' borderColor='#939393'>
                        <Text fontWeight='bold' fontSize='18px'>{titleBox}</Text>
                        <Text mt='15px'>{textContentBox}</Text>
                    </Box>
                </GridItem>
                <GridItem pl='2' mt={{ base: '0px', lg: '20px', md: '20px', sm: '0px' }} area={'footer'} textAlign='left' fontSize='12px' paddingLeft='40px' paddingRight='40px'>
                </GridItem>
            </Grid>
        </Box>
    );
}

export default CV2Metrics;
